/**
 * Utility functions for golf score calculations and formatting
 */

/**
 * Get a descriptive label for a golf score based on the score and par
 * @param score The player's score
 * @param par The par for the hole
 * @param t Translation function (optional)
 * @returns A string label describing the score (e.g., "Birdie", "Eagle", etc.)
 */
export const getScoreLabel = (score: number, par: number, t?: (key: string) => string): string => {
  const diff = score - par;
  if (score === 1) return t ? t('hole_in_one') : 'Hole in One';
  if (diff === -3) return 'Double Eagle';
  if (diff === -2) return 'Eagle';
  if (diff === -1) return 'Birdie';
  if (diff === 0) return 'Par';
  if (diff === 1) return 'Bogey';
  if (diff === 2) return 'Double Bogey';
  if (diff === 3) return 'Triple Bogey';
  if (diff === 4) return 'Quadruple Bogey';
  return `${diff > 0 ? diff : Math.abs(diff)} ${diff > 0 ? 'Over' : 'Under'} Par`;
};

/**
 * Get a CSS class for styling a golf score based on the score and par
 * @param score The player's score
 * @param par The par for the hole
 * @returns A CSS class name for styling the score
 */
export const getScoreClass = (score: number, par: number): string => {
  if (!score || !par) return '';

  const diff = score - par;
  if (score === 1) return 'text-yellow-500'; // Hole in one !
  if (diff === -3) return 'text-orange-500 border-4 border-orange-500 rounded-full border-double'; // Double Eagle
  if (diff === -2) return 'text-purple-500 border-4 border-purple-500 rounded-full border-double'; // Eagle
  if (diff === -1) return 'text-orange-500 border-2 border-orange-500 rounded-full'; // Birdie
  if (diff === 0) return 'text-green-500'; // Par
  if (diff === 1) return 'text-blue-500 border-2 border-blue-500'; // Bogey
  if (diff === 2) return 'text-blue-500 border-4 border-blue-500 border-double'; // Double Bogey

  if (diff > 2) return 'text-red-500';

  return 'text-green-500';
};
